import { useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import CachedIcon from '@material-ui/icons/Cached';
import EditIcon from '@material-ui/icons/Edit';
import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { Link, MarkdownContent } from '@backstage/core-components';
import { alertApiRef, errorApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import {
  catalogApiRef,
  EntityInfoCard,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';

/*
 * The mock's About card: a description and the entity's tags, and nothing else.
 *
 * The vendor card also carries Owner, Domain, System, Parent component, Kind,
 * Type and Lifecycle as a field grid, above a VIEW SOURCE / VIEW TECHDOCS link
 * row. After the rail restructure every one of those is stated somewhere else —
 * Owner in the Ownership card, System and the rest in Relations, Kind in the
 * eyebrow above the title, TechDocs as a tab — so the card was mostly a second
 * copy of its own page.
 *
 * Rebuilt rather than skinned: `AboutContent` renders that field set with no
 * way to omit a field, and the fields are conditional on kind, so hiding them
 * by position would break on the next entity. The refresh and edit affordances
 * are reproduced from the vendor card, permission gate included.
 *
 * Measured from the mock: description 13.5px/1.65 #363636; tags DM Mono 11px
 * #363636 on #f4f4f4, pill radius, padding 2px 9px, 8px gap.
 */
const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  description: {
    wordBreak: 'break-word',
    fontSize: 13.5,
    lineHeight: 1.65,
    color: 'var(--hone-portal-fg-body)',
    '& > *:first-child': { marginTop: 0 },
    '& > *:last-child': { marginBottom: 0 },
  },
  tags: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  tag: {
    fontFamily: 'var(--bui-font-monospace)',
    fontSize: 11,
    lineHeight: 1.6,
    color: 'var(--hone-portal-fg-body)',
    background: 'var(--hone-portal-fill-inset)',
    borderRadius: 999,
    padding: '2px 9px',
  },
});

export function EntityAboutCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );

  const editUrl = entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
  const location = entity.metadata.annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    location?.startsWith('url:') || location?.startsWith('file:');
  const tags = entity.metadata.tags ?? [];

  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: 'Refresh scheduled',
        severity: 'info',
        display: 'transient',
      });
    } catch (e) {
      errorApi.post(e as Error);
    }
  }, [catalogApi, entity, alertApi, errorApi]);

  /*
   * No source link here: it is the header's "Repo" button now, and the mock
   * shows it in exactly one place.
   */
  const headerActions = (
    <>
      {allowRefresh && canRefresh && (
        <IconButton
          title="Schedule entity refresh"
          aria-label="Schedule entity refresh"
          onClick={refreshEntity}
        >
          <CachedIcon />
        </IconButton>
      )}
      {editUrl && (
        <IconButton
          component={Link}
          to={editUrl}
          title="Edit metadata"
          aria-label="Edit metadata"
        >
          <EditIcon />
        </IconButton>
      )}
    </>
  );

  return (
    <EntityInfoCard title="About" headerActions={headerActions}>
      <div className={classes.root}>
        <MarkdownContent
          className={classes.description}
          content={entity.metadata.description ?? 'No description'}
        />
        {tags.length > 0 && (
          <div className={classes.tags}>
            {tags.map(tag => (
              <span className={classes.tag} key={tag}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </EntityInfoCard>
  );
}
